import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, MapPin, Linkedin, Send, Clock, CheckCircle2, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import ReCAPTCHA from 'react-google-recaptcha';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_release-nexus/artifacts/a0bdi5ug_Alignk%20backlink%20professional.png';
const API_URL = process.env.REACT_APP_BACKEND_URL;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const Contact = () => {
  const recaptchaRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
  };

  const handleCaptchaExpired = () => {
    setCaptchaToken(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate terms acceptance
    if (!acceptedTerms) {
      toast.error('Please accept the Terms of Service and Privacy Policy to continue.');
      return;
    }
    
    // Validate captcha
    if (RECAPTCHA_SITE_KEY && !captchaToken) {
      toast.error('Please complete the CAPTCHA verification.');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API_URL}/api/contact`, {
        ...formData,
        captcha_token: captchaToken
      });
      
      if (response.data.success) {
        setIsSubmitted(true);
        toast.success('Message sent successfully! We\'ll get back to you soon.');
        
        // Reset form after submission
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
        setAcceptedTerms(false);
        setCaptchaToken(null);
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
        
        // Reset success state after 5 seconds
        setTimeout(() => setIsSubmitted(false), 5000);
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error(error.response?.data?.detail || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <MapPin className="h-6 w-6" style={{ color: '#4AC5DB' }} />,
      title: 'Office Address',
      details: [
        '71-75 Shelton Street',
        'Covent Garden',
        'London WC2H 9JQ',
        'United Kingdom'
      ]
    },
    {
      icon: <Phone className="h-6 w-6" style={{ color: '#4AC5DB' }} />,
      title: 'Call Us',
      details: ['+44 (0) 20323 97095'],
      link: 'tel:+442032397095'
    },
    {
      icon: <Mail className="h-6 w-6" style={{ color: '#4AC5DB' }} />,
      title: 'Email Us',
      details: ['support@alignk.com'],
      link: 'mailto:support@alignk.com'
    },
    {
      icon: <Clock className="h-6 w-6" style={{ color: '#4AC5DB' }} />,
      title: 'Business Hours',
      details: [
        'Monday - Friday: 9:00 AM - 6:00 PM (GMT)',
        'Saturday - Sunday: Closed'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center">
              <img src={LOGO_URL} alt="Alignk" className="h-10" />
            </Link>
            <Link to="/">
              <Button variant="outline" className="flex items-center gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-cyan-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Playfair Display' }}>
              Get in <span style={{ color: '#4AC5DB' }}>Touch</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Have questions about our press release distribution services? We&apos;re here to help. Reach out to our team and we&apos;ll get back to you as soon as possible.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            
            {/* Contact Information */}
            <div>
              <h2 className="text-2xl font-bold mb-8" style={{ fontFamily: 'Playfair Display' }}>
                Contact <span style={{ color: '#4AC5DB' }}>Information</span>
              </h2>
              
              <div className="space-y-8">
                {contactInfo.map((info, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-cyan-50 flex items-center justify-center">
                      {info.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">{info.title}</h3>
                      {info.link ? (
                        <a 
                          href={info.link} 
                          className="text-gray-600 hover:text-cyan-600 transition"
                        >
                          {info.details[0]}
                        </a>
                      ) : (
                        <div className="text-gray-600 space-y-1">
                          {info.details.map((detail, idx) => (
                            <p key={idx}>{detail}</p>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Social Links */}
              <div className="mt-10 pt-8 border-t border-gray-200">
                <h3 className="font-semibold text-lg mb-4">Connect With Us</h3>
                <div className="flex flex-wrap gap-3">
                  <a 
                    href="https://www.linkedin.com/company/alignkx/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-[#0077B5] text-white rounded-lg hover:bg-[#006097] transition"
                  >
                    <Linkedin className="h-5 w-5" />
                    LinkedIn
                  </a>
                  <a 
                    href="https://www.instagram.com/alignk.x/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] text-white rounded-lg hover:opacity-90 transition"
                  >
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                    Instagram
                  </a>
                </div>
              </div>

              {/* Map Placeholder */}
              <div className="mt-10">
                <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl p-8 text-center">
                  <MapPin className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-600 font-medium">71-75 Shelton Street</p>
                  <p className="text-gray-500">Covent Garden, London WC2H 9JQ</p>
                  <a 
                    href="https://www.google.com/maps/search/?api=1&query=71-75+Shelton+Street+Covent+Garden+London+WC2H+9JQ" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-block mt-4 text-cyan-600 hover:text-cyan-700 font-medium"
                  >
                    View on Google Maps →
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <div className="bg-gradient-to-br from-gray-50 to-cyan-50 rounded-2xl p-8 shadow-lg">
                <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Playfair Display' }}>
                  Send us a <span style={{ color: '#4AC5DB' }}>Message</span>
                </h2>
                <p className="text-gray-600 mb-8">
                  Fill out the form below and our team will respond within 24 hours.
                </p>

                {isSubmitted ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="h-8 w-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-green-600 mb-2">Message Sent!</h3>
                    <p className="text-gray-600">Thank you for reaching out. We&apos;ll get back to you soon.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Your Name *
                        </label>
                        <Input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="John Doe"
                          required
                          className="w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email Address *
                        </label>
                        <Input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="john@example.com"
                          required
                          className="w-full"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Subject *
                      </label>
                      <Input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder="How can we help you?"
                        required
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Message *
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us more about your inquiry..."
                        required
                        rows={5}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 resize-none"
                        data-testid="contact-message"
                      />
                    </div>

                    {/* Terms and Privacy Checkbox */}
                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="terms-checkbox"
                        checked={acceptedTerms}
                        onCheckedChange={(checked) => setAcceptedTerms(checked)}
                        className="mt-1"
                        data-testid="terms-checkbox"
                      />
                      <label 
                        htmlFor="terms-checkbox" 
                        className="text-sm text-gray-600 cursor-pointer leading-relaxed"
                      >
                        I agree to the{' '}
                        <Link to="/terms-of-use" className="text-cyan-600 hover:text-cyan-700 underline" target="_blank">
                          Terms of Service
                        </Link>
                        {' '}and{' '}
                        <Link to="/privacy-policy" className="text-cyan-600 hover:text-cyan-700 underline" target="_blank">
                          Privacy Policy
                        </Link>
                        . *
                      </label>
                    </div>

                    {/* reCAPTCHA */}
                    {RECAPTCHA_SITE_KEY && (
                      <div className="flex justify-center" data-testid="recaptcha-container">
                        <ReCAPTCHA
                          ref={recaptchaRef}
                          sitekey={RECAPTCHA_SITE_KEY}
                          onChange={handleCaptchaChange}
                          onExpired={handleCaptchaExpired}
                        />
                      </div>
                    )}

                    <Button
                      type="submit"
                      disabled={isSubmitting || !acceptedTerms || (RECAPTCHA_SITE_KEY && !captchaToken)}
                      className="w-full py-6 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ backgroundColor: '#4AC5DB' }}
                      data-testid="contact-submit-btn"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center gap-2">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          Sending...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          <Send className="h-5 w-5" />
                          Send Message
                        </span>
                      )}
                    </Button>
                  </form>
                )}
              </div>

              {/* Quick Response Promise */}
              <div className="mt-6 p-6 bg-gradient-to-r from-cyan-500 to-teal-500 rounded-xl text-white">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <Clock className="h-8 w-8" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Quick Response Guarantee</h3>
                    <p className="text-cyan-50 text-sm">
                      We aim to respond to all inquiries within 24 hours during business days. For urgent matters, call us at +44 (0) 20323 97095 or email support@alignk.com.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ CTA Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>
            Looking for Quick Answers?
          </h2>
          <p className="text-gray-600 mb-6">
            Check out our frequently asked questions for instant answers to common queries.
          </p>
          <Link to="/faq">
            <Button variant="outline" size="lg" className="px-8">
              Visit FAQ Page
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <img 
                src={LOGO_URL} 
                alt="Alignk" 
                className="h-12 mb-4 brightness-0 invert" 
                style={{ filter: 'brightness(0) invert(1)' }}
              />
              <p className="text-gray-400 text-sm">
                Premium press release distribution services for businesses and agencies.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/about" className="hover:text-white transition">About Us</Link></li>
                <li><Link to="/contact" className="hover:text-white transition">Contact</Link></li>
                <li><Link to="/faq" className="hover:text-white transition">FAQ</Link></li>
                <li><Link to="/privacy-policy" className="hover:text-white transition">Privacy Policy</Link></li>
                <li><Link to="/terms-of-use" className="hover:text-white transition">Terms of Use</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Services</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>Press Release Distribution</li>
                <li>Media Outreach</li>
                <li>Digital PR Services</li>
                <li>Brand Visibility</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Contact</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>71-75 Shelton Street, Covent Garden, London WC2H 9JQ</span>
                </li>
                <li>
                  <a href="tel:+442032397095" className="hover:text-white transition flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    +44 (0) 20323 97095
                  </a>
                </li>
                <li>
                  <a href="mailto:support@alignk.com" className="hover:text-white transition flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    support@alignk.com
                  </a>
                </li>
                <li>
                  <a href="https://www.linkedin.com/company/alignkx/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition flex items-center gap-2">
                    <Linkedin className="w-4 h-4" />
                    LinkedIn
                  </a>
                </li>
                <li>
                  <a href="https://www.instagram.com/alignk.x/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                    Instagram
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-sm text-gray-400">
            <p>&copy; {new Date().getFullYear()} Alignk. All rights reserved.</p>
            <p className="mt-3 text-xs text-gray-500">
              Alignk works with third-party service providers for digital promotion and advertising. Alignk is not affiliated with or endorsed by Google or any public figures.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Contact;
